import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { getListingDetail, updateMyListing, getSiteCategories, FrontendCategory } from "@/utils/api";
import request from "@/utils/api";

// 获取当前站点ID
const getSiteId = () => localStorage.getItem('current_site_id') || '';

export default function EditListingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState<{ id: string; name: string; icon: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState("");

  const [formData, setFormData] = useState({
    categoryId: "",
    title: "",
    content: "",
    companyName: "",
    contactName: "",
    contactPhone: "",
    contactWechat: "",
  });
  const [images, setImages] = useState<string[]>([]);

  // 下载远程图片（通过后端代理）
  const downloadImage = async (url: string): Promise<string> => {
    try {
      const result = await request<{ url: string }>('/api/sites/' + SITE_ID + '/fetch-image', {
        params: { url },
      });
      if (result && (result as any).url) {
        return (result as any).url;
      }
      return url;
    } catch (err) {
      console.error("下载图片失败:", err);
      return url;
    }
  };

  // 上传图片到服务器
  const uploadImage = async (base64Data: string): Promise<string> => {
    try {
      const response = await fetch(base64Data);
      const blob = await response.blob();
      const ext = blob.type.split('/')[1] || 'png';
      const filename = `image_${Date.now()}.${ext}`;

      const formDataUpload = new FormData();
      formDataUpload.append('file', blob, filename);

      const result = await request<{ url: string }>('/upload', {
        method: 'POST',
        body: formDataUpload as any,
      });

      if (result && (result as any).url) {
        return (result as any).url;
      }
      return base64Data;
    } catch (err) {
      console.error("图片上传失败:", err);
      return base64Data;
    }
  };

  // 处理粘贴事件
  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    let hasImages = false;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        hasImages = true;
        break;
      }
    }

    if (hasImages) {
      e.preventDefault();
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          if (images.length >= 6) {
            setError("最多上传6张图片");
            setTimeout(() => setError(""), 3000);
            break;
          }
          const file = items[i].getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              const base64 = event.target?.result as string;
              setImages((prev) => [...prev, base64]);
            };
            reader.readAsDataURL(file);
          }
        }
      }
      return;
    }

    const html = clipboardData.getData("text/html");
    if (html) {
      const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
      const matches = [...html.matchAll(imgRegex)];

      if (matches.length > 0) {
        e.preventDefault();
        let plainText = clipboardData.getData("text/plain");

        const lines = plainText.split('\n');
        const filteredLines = lines.filter(line => {
          const trimmed = line.trim();
          if (/^[\w\-]+\.(png|jpg|jpeg|gif|webp|bmp)(\s*\.png)?$/i.test(trimmed)) {
            return false;
          }
          if (trimmed === '') return false;
          return true;
        });
        plainText = filteredLines.join('\n');

        const imageUrls = matches.map((m) => m[1]).filter((url) => url.startsWith("http"));

        if (images.length + imageUrls.length > 6) {
          setError(`最多上传6张图片`);
          setTimeout(() => setError(""), 3000);
          setFormData((prev) => ({ ...prev, content: prev.content + plainText }));
          return;
        }

        for (const url of imageUrls) {
          const downloadedImage = await downloadImage(url);
          setImages((prev) => [...prev, downloadedImage]);
        }

        setFormData((prev) => ({ ...prev, content: prev.content + plainText }));
      }
    }
  }, [images]);

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      
      try {
        const [catsData, listing] = await Promise.all([
          getSiteCategories(),
          getListingDetail(id),
        ]);
        
        setCategories(catsData.filter((c) => !c.parent_id));
        setFormData({
          categoryId: listing.category_id || "",
          title: listing.title || "",
          content: listing.content || "",
          companyName: (listing as any).company_name || "",
          contactName: (listing as any).contact_name || "",
          contactPhone: listing.contact_phone || "",
          contactWechat: listing.contact_wechat || "",
        });
        setImages(listing.images || []);
      } catch (err: any) {
        setError(err.message || "加载失败");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;
      Array.from(files).forEach(file => {
        if (images.length >= 6) {
          setError("最多上传6张图片");
          setTimeout(() => setError(""), 3000);
          return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target?.result as string;
          setImages((prev) => [...prev, base64]);
        };
        reader.readAsDataURL(file);
      });
    };
    input.click();
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.categoryId) {
      setError("请选择分类");
      return;
    }
    if (!formData.title.trim()) {
      setError("请输入标题");
      return;
    }
    if (!formData.contactName.trim()) {
      setError("请输入联系人姓名");
      return;
    }
    if (!formData.contactPhone.trim()) {
      setError("请输入联系电话");
      return;
    }
    if (!formData.content.trim()) {
      setError("请输入详情内容");
      return;
    }
    
    setIsSubmitting(true);
    setUploadProgress("正在上传图片...");

    try {
      const finalImages: string[] = [];
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        setUploadProgress(`正在上传图片 ${i + 1}/${images.length}...`);
        if (img.startsWith('data:')) {
          const uploadedUrl = await uploadImage(img);
          finalImages.push(uploadedUrl);
        } else {
          finalImages.push(img);
        }
      }

      setUploadProgress("正在保存信息...");

      await updateMyListing(id!, {
        category_id: formData.categoryId,
        title: formData.title,
        content: formData.content,
        images: finalImages.length > 0 ? finalImages : undefined,
        company_name: formData.companyName || undefined,
        contact_name: formData.contactName || undefined,
        contact_phone: formData.contactPhone,
        contact_wechat: formData.contactWechat || undefined,
      });
      
      navigate("/dashboard/my-listings");
    } catch (err: any) {
      setError(err.message || "保存失败，请重试");
    } finally {
      setIsSubmitting(false);
      setUploadProgress("");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full"></div>
          <span className="ml-3 text-gray-500">加载中...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error && !formData.title) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50">
            <i className="ri-error-warning-line text-2xl text-red-400" />
          </div>
          <p className="text-gray-500 mb-4">{error}</p>
          <button 
            onClick={() => navigate("/dashboard/my-listings")} 
            className="inline-flex items-center px-5 py-2.5 bg-orange-500 text-white rounded-full text-sm hover:bg-orange-600 transition-colors cursor-pointer"
          >
            返回我的发布
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-5 md:p-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/dashboard/my-listings")}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-gray-600" />
          </button>
          <h1 className="text-lg md:text-xl font-bold text-gray-800">编辑信息</h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* 左右两栏布局 */}
          <div className="flex flex-col lg:flex-row">
            {/* 左侧 - 基本信息 */}
            <div className="flex-1 p-5 md:p-8 border-b lg:border-b-0 lg:border-r border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <i className="ri-file-text-line text-orange-500"></i>
                基本信息
              </h3>

              {/* 分类标签选择 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  选择分类 <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, categoryId: cat.id }))}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                        formData.categoryId === cat.id
                          ? 'bg-orange-500 text-white shadow-md shadow-orange-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {cat.icon && <i className={`${cat.icon} mr-1`}></i>}
                      {cat.name}
                    </button>
                  ))}
                </div>
                {formData.categoryId && (
                  <p className="text-xs text-green-600 mt-2">
                    <i className="ri-check-line mr-1"></i>已选择
                  </p>
                )}
              </div>

              {/* 标题 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  信息标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="例如：专业企业官网定制开发"
                  maxLength={100}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.title.length}/100</p>
              </div>

              {/* 联系信息 */}
              <div className="mb-5">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-1.5">
                  <i className="ri-contacts-line text-gray-400"></i>
                  联系方式
                </h4>
                <div className="space-y-3">
                  <div className="relative">
                    <i className="ri-building-2-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="请输入公司名称（选填）"
                      maxLength={100}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                  <div className="relative">
                    <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      placeholder="请输入联系人姓名"
                      maxLength={50}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                  <div className="relative">
                    <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="contactPhone"
                      value={formData.contactPhone}
                      onChange={handleChange}
                      placeholder="请输入联系电话"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                  <div className="relative">
                    <i className="ri-wechat-fill absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="contactWechat"
                      value={formData.contactWechat}
                      onChange={handleChange}
                      placeholder="请输入微信号（选填）"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 右侧 - 详情和图片 */}
            <div className="flex-1 p-5 md:p-8 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
                <i className="ri-image-fill text-orange-500"></i>
                详情与图片
              </h3>

              {/* 图片上传 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  上传图片 <span className="text-gray-400 font-normal">（最多6张）</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                      <img 
                        src={img.startsWith('data:') ? img : `/api${img}`} 
                        alt={`upload-${idx}`} 
                        className="w-full h-full object-cover" 
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                  ))}
                  {images.length < 6 && (
                    <button
                      type="button"
                      onClick={handleImageSelect}
                      className="w-20 h-20 md:w-24 md:h-24 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 hover:border-orange-400 hover:bg-orange-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-camera-line text-2xl text-gray-400" />
                      <span className="text-xs text-gray-400 mt-1">添加图片</span>
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  <i className="ri-information-line mr-1"></i>
                  支持从网页复制图片粘贴到这里
                </p>
              </div>

              {/* 详情内容 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  详情内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  onPaste={handlePaste}
                  placeholder="详细描述您的服务内容、优势、价格等信息..."
                  rows={10}
                  maxLength={5000}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.content.length}/5000</p>
              </div>
            </div>
          </div>

          {/* 底部操作栏 */}
          <div className="px-5 md:px-8 py-4 bg-gray-50 border-t border-gray-100">
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate("/dashboard/my-listings")}
                className="px-6 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors text-sm cursor-pointer"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 text-sm shadow-lg shadow-orange-200 cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    {uploadProgress || "保存中..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-save-line"></i>
                    保存修改
                  </span>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
